import { useMemo, useState } from "react";
import {
  CheckPicker,
  Input,
  InputNumber,
  Tag,
  TagGroup,
  TagPicker,
  Tooltip,
  Whisper,
} from "rsuite";
import BrieDomainMapperTable from "../../BrieComponents/BrieDomainMapperTable";
import useRecords from "../../../../api/api-hooks/records/useRecords";
import useDomainMapper, {
  MappedDomain,
} from "../../../../api/api-hooks/brie/useDomainMapper";
import AppSpinner from "../../../../elements/AppSpinner";
import { Button } from "reactstrap";

const DomainMapper = () => {
  const [excludeOptions, setExcludeOptions] = useState<[] | string[]>([]);
  const [typing, setTyping] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const { records: companies } = useRecords({ moduleAPIName: "Companies" });
  const [selectedCompanies, setSelectedCompanies] = useState<any>();
  const [maximumContacts, setMaximumContacts] = useState<number | undefined>();
  const [valuesMappedActive, setValuesMappedActive] = useState<boolean>(false);
  const [checkedKeys, setCheckedKeys] = useState([]);

  const {
    domainMapper,
    domainMapperLoading,
    domainMapperError,
    domainMapperValue,
    domainMapperApply,
    domainMapperApplyLoading,
    domainMapperApplyError,
  } = useDomainMapper();
  const removeExcludeOption = (excludeOption: string) => {
    const nextTags = excludeOptions.filter((item) => item !== excludeOption);
    setExcludeOptions(nextTags);
  };
  const addExcludeOption = () => {
    const nextTags = inputValue
      ? [...excludeOptions, inputValue]
      : excludeOptions;
    setExcludeOptions(nextTags);
    setTyping(false);
    setInputValue("");
  };

  const handleButtonClick = () => {
    setTyping(true);
  };

  const handleRestart = () => {
    setExcludeOptions([]);
    setTyping(false);
    setInputValue("");
    setSelectedCompanies([]);
    setMaximumContacts(undefined);
    setValuesMappedActive(false);
  };

  const selectedMappedDomains = useMemo(() => {
    if (
      !checkedKeys ||
      !checkedKeys.length ||
      !domainMapperValue ||
      !domainMapperValue.length
    )
      return;

    const mappedDomains = checkedKeys.map((key) => {
      const foundDomain = domainMapperValue.find(
        (domain: MappedDomain) => domain.key === key
      );
      return foundDomain;
    });

    return mappedDomains;
  }, [checkedKeys]);

  const handleApplyDomainMapper = () => {
    if (!selectedMappedDomains || !selectedMappedDomains.length) return;

    domainMapperApply({ selectedMappedDomains });
    handleRestart();
  };

  const handleSelectCompanies = (companies: any) => {
    setSelectedCompanies(companies);
  };

  const handleCheckAll = (value: any, checked: any) => {
    const keys: any = checked
      ? domainMapperValue.map((item: any) => item.key)
      : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: any, checked: any) => {
    const keys: any = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  const renderInput = () => {
    if (typing) {
      return (
        <Input
          size="xs"
          style={{ width: 70, marginLeft: "10px", marginTop: "10px" }}
          value={inputValue}
          onChange={setInputValue}
          onBlur={addExcludeOption}
          onPressEnter={addExcludeOption}
          disabled={domainMapperValue && valuesMappedActive}
        />
      );
    }

    return (
      <div
        className="ml-3 h-auto text-white d-flex align-items-center"
        style={{
          backgroundColor:
            valuesMappedActive && domainMapperValue ? "#e5e5ea" : "#26B058",
          borderRadius: "6px",
          marginTop: "10px",
          lineHeight: "20px",
          fontSize: "12px",
          padding: "2px 8px",
        }}
        onClick={() => {
          if (valuesMappedActive && domainMapperValue) {
            return;
          }
          handleButtonClick();
        }}
      >
        <i className="mdi mdi-plus mr-1" style={{ fontSize: "16px" }}></i>{" "}
        <span>Add</span>
      </div>
    );
  };

  const optionCompanies = useMemo(() => {
    if (!companies?.length) return [];
    const options = companies.map((company: any) => {
      return {
        label: company.Name,
        value: company.id,
      };
    });

    return options;
  }, [companies]);

  const handleFindMatches = () => {
    if (!selectedCompanies) return;
    setValuesMappedActive(true);
    domainMapper({ selectedCompanies, maximumContacts, excludeOptions });
  };

  const handleSetMaximumContacts = (number: string) => {
    setMaximumContacts(Number(number));
  };

  return (
    <>
      <div
        className="d-flex flex-column"
        style={{
          boxShadow: "0px 1px 8px 2px rgba(0, 0, 0, 0.15)",
          padding: "24px",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <div className="d-flex w-100 align-items-center mb-3">
          <i
            className="mdi mdi-relation-one-to-one mr-3"
            style={{ fontSize: "32px" }}
          ></i>
          <h2 className="m-0">Domain Mapper</h2>
        </div>
        <p
          className="m-0 mb-1"
          style={{
            fontSize: "13px",
            fontWeight: 400,
            letterSpacing: "0.0025em",
          }}
        >
          Link contacts to companies based on email domains.
        </p>
        <div className="d-flex my-3 align-items-center">
          <span className="font-weight-bold text-sm mr-2">
            Select Companies
          </span>
          <i
            className="mdi mdi-asterisk"
            style={{ fontSize: "12px", color: "red" }}
          ></i>
        </div>
        <TagPicker
          data={optionCompanies}
          creatable
          className="w-100"
          onChange={(companies) => handleSelectCompanies(companies)}
          value={selectedCompanies}
          disabled={domainMapperValue && valuesMappedActive}
        />
        <span className="font-weight-bold text-sm mt-5 mb-2">
          Set Mapping Criteria
        </span>
        <div className="d-flex justify-content-between">
          <span className=" text-sm my-3">
            Maximum number of contacts
            <Whisper
              trigger="click"
              placement="auto"
              speaker={
                <Tooltip>
                  (Optional) Select the máximum amount of results. Leave blank
                  if you want all the results.
                </Tooltip>
              }
            >
              <i
                className="mdi mdi-information-slab-circle-outline ml-2"
                style={{ fontSize: "12px" }}
              ></i>
            </Whisper>
          </span>
          <div style={{ width: "20%" }}>
            <InputNumber
              value={maximumContacts || ""}
              onChange={(num: any) => handleSetMaximumContacts(num)}
              disabled={domainMapperValue && valuesMappedActive}
            />
          </div>
        </div>
        <div className="d-flex justify-content-between">
          <span className=" text-sm my-3">
            Select NAICS Code
            <Whisper
              trigger="click"
              placement="auto"
              speaker={<Tooltip>(Optional) Select the NAICS code.</Tooltip>}
            >
              <i
                className="mdi mdi-information-slab-circle-outline ml-2"
                style={{ fontSize: "12px" }}
              ></i>
            </Whisper>
          </span>
          <div style={{ width: "20%" }}>
            <CheckPicker disabled data={[]} className="w-100" />
          </div>
        </div>
        <div className="d-flex flex-column">
          <div className="my-3">
            <span className="text-sm">Exclude</span>
            <Whisper
              trigger="click"
              placement="auto"
              speaker={
                <Tooltip>
                  (Optional) Add words that will be used to exclude contact
                  emails if emails include them.
                </Tooltip>
              }
            >
              <i
                className="mdi mdi-information-slab-circle-outline ml-2"
                style={{ fontSize: "12px" }}
              ></i>
            </Whisper>
          </div>
          <TagGroup className="d-flex flex-wrap">
            {excludeOptions.map((item, index) => {
              return (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeExcludeOption(item)}
                >
                  {item}
                </Tag>
              );
            })}
            {renderInput()}
          </TagGroup>
        </div>
        {domainMapperLoading ? (
          <AppSpinner className="ml-auto mt-5" size="md" />
        ) : (
          <>
            {!domainMapperValue || !valuesMappedActive ? (
              <div className="mb-3 d-flex justify-content-end ml-auto mt-5">
                <Button
                  onClick={() => handleFindMatches()}
                  color="primary"
                  size="sm"
                  className="m-0 p-0"
                  style={{ width: "100px", height: "32px", marginLeft: "10px" }}
                  disabled={
                    !selectedCompanies?.length ||
                    typing ||
                    (domainMapperValue?.length && valuesMappedActive)
                  }
                >
                  Find Matches
                </Button>
              </div>
            ) : null}
          </>
        )}
        {domainMapperValue &&
          domainMapperValue.length &&
          valuesMappedActive && (
            <>
              <span
                className="font-weight-bolder text-primary mt-5 mb-3"
                style={{ fontSize: "16px" }}
              >
                Mapping Recommendations:
              </span>
              <BrieDomainMapperTable
                mappedData={domainMapperValue}
                handleCheckAll={handleCheckAll}
                handleCheck={handleCheck}
                checkedKeys={checkedKeys}
              />
              {domainMapperApplyLoading ? (
                <AppSpinner className="ml-auto" size="md" />
              ) : (
                <div className="my-5 d-flex justify-content-end">
                  <Button
                    onClick={() => handleRestart()}
                    size="sm"
                    className="m-0 p-0 mr-3"
                    outline
                    style={{
                      width: "65px",
                      height: "32px",
                      marginLeft: "10px",
                    }}
                  >
                    Restart
                  </Button>
                  <Button
                    onClick={() => handleApplyDomainMapper()}
                    color="primary"
                    size="sm"
                    className="m-0 p-0"
                    style={{
                      width: "120px",
                      height: "32px",
                      marginLeft: "10px",
                    }}
                    disabled={
                      !checkedKeys ||
                      !checkedKeys.length ||
                      !selectedMappedDomains ||
                      !selectedMappedDomains.length
                    }
                  >
                    Apply Mapping
                  </Button>
                </div>
              )}
            </>
          )}
      </div>
    </>
  );
};

export default DomainMapper;
